import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { debounce } from "lodash";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import TextAreaFieldComponent from "../../../components/inputs/TextAreafield/TextAreafieldComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetPaymentTypesQuery } from "../../../hooks/useGetPaymentTypesQuery";
import { resetStudentData, setStudentData } from "../../../redux/slices/school/student";
import { resetUserData, setUserData } from "../../../redux/slices/users/user";
import { RootState } from "../../../redux/store";
import { routes } from "../../../routes";
import { createStudentDebt, updateStudentDebt } from "../../../services/finance/studentDebt";
import { getStudentByCode } from "../../../services/school/student/getStudentByCode";
import { getUserById } from "../../../services/user/getUsers";
import { StudentDebtType } from "../../../types/Finance";
import { UserPreview } from "../../Users/UserPreview";
import { CreateStudentDebtFormValidation } from "./CreateStudentDebtFormValidation";
import { resetStudentDebtData } from "../../../redux/slices/finance/studentDebt";
import { resetPreviousPath } from "../../../redux/slices/previousPath";
import { months, monthsTranslation } from "../../../utils/months";

export const UpdateStudentDebt = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mutation = useMutation({ mutationFn: updateStudentDebt, mutationKey: 'updateStudentDebt' });
  const { data:paymentTypesData} = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 4000 });
  const userData = useSelector((state: RootState) => state.user);
  const studentDebt = useSelector((state: RootState) => state.studentDebt);
  const defaultValues:StudentDebtType=studentDebt;
  const previousPath = useSelector((state: RootState) => state.previousPath);

  const { control, handleSubmit, formState: { errors },setValue,getValues } = useForm<StudentDebtType>
  (
    {
      resolver: yupResolver(CreateStudentDebtFormValidation),
      defaultValues,
      mode: 'onBlur',
      reValidateMode: 'onBlur',
      shouldUseNativeValidation: false,
    }
  );
  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("Cobrança criada!");
        navigate(routes.studentDebts);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = () => {
    dispatch(resetStudentDebtData());
    const path = previousPath?.previousPath;
    if(path){
      navigate(path)
      dispatch(resetPreviousPath())
      return;
    }
    
    navigate(routes.studentDebts);
    dispatch(resetStudentData());
    dispatch(resetUserData());
  }
  const { isLoading: isLoadingGetStudent, refetch: fetchStudentByCode, isError: isErrorGetStudent, error } = useQuery(
    ['getStudentByCode'],
    async () => {
      const studentCodeValue = getValues('studentCode');

      // if (studentCodeValue.trim().length < 17) return;
      try {
          const studentData = await getStudentByCode(studentCodeValue);
          if(!studentData) return;
          const userData = await getUserById(studentData.idUser)
          if(!userData) return;
          dispatch(setStudentData(studentData));
          dispatch(setUserData(userData));
          setValue('idStudent', studentData.id!);
          setValue('fullname', userData.name);
          if(studentCodeValue){
          //  fetchStudentDebts();
          }
        
      } catch (error) {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const handleStudentCodeChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue('studentCode', trimmedValue);
      if (trimmedValue.length >= 3) {
        fetchStudentByCode();
      }else{
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    }, 300),
    [setValue, fetchStudentByCode]
  );
  const handleSelectPaymentType = (idPayment:string) =>{
    const paymentType = paymentTypesData?.items.find(i=> i.id===idPayment);
    if(paymentType){
      setValue('currentAmount', paymentType.amount)
      setValue('originalAmount', paymentType.amount)
    }
  }
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>
        <TextFieldComponent
            name="studentCode"
            label="Código do estudante"
            control={control}
            errors={errors}
            fieldProps={{
              placeholder: 'Digite o código do estudante',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                handleStudentCodeChange(pastedText);
              }
            }}
            required={true}
          />
          {userData && userData.id && <>
            <UserPreview/>
          </>}
           <SelectComponent name="idPaymentType" label="Tipo de emolumento" control={control} errors={errors} style={{ width: '100%' }}
            options={paymentTypesData?.items.map((paymentType) => ({
              value:  paymentType?.id?? "",
              label: paymentType?.description ?? ""
            }))}
            onSelect={handleSelectPaymentType}
            required={true}
          />
          <TextFieldComponent name="originalAmount" label="Valor a pagar" control={control} errors={errors} fieldProps={{ placeholder: 'Valor a pagar' }} required={true}/>
          <TextFieldComponent name="currentAmount" label="Valor a pagar remanescente" control={control} errors={errors} fieldProps={{ placeholder: 'Valor a pagar remanescente' }} required={true}/>
          <SelectComponent
            label="Mês a cobrar"
            options={months.map((month) => ({
              value: month,
              label: monthsTranslation[month as keyof typeof monthsTranslation] || month
            }))}
            errors={errors}
            control={control}
            name={"month"}
          />
          <DateFieldComponent name="dueDate" label="Data de vencimento" control={control} errors={errors} style={{ width: '100%' }} required={true}/>
          <TextAreaFieldComponent
                name="description"
                label="Descrição"
                control={control}
                errors={errors}
                fieldProps={{ placeholder: 'Descrição' }}
                style={{ width: '100%' }}
              />
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'  >
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}
